import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Drawer,
    IconButton,
    Tabs,
    Tab,
    Typography,
    CircularProgress,
    Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useDispatch } from 'react-redux';
import { fetchStarredMessages } from 'src/redux/slices/whatsappV2';
import useScrollToMessage from 'src/hooks/useScrollToMessage';
import { COLORS } from './styles';
import { parsedDateTimeForWhatsapp } from './whatsappUtil';

const SCOPE = Object.freeze({ THIS_CHAT: 'THIS_CHAT', ALL_CHATS: 'ALL_CHATS' });

const drawerPaperSx = {
    width: { xs: '100%', sm: 380 },
    backgroundColor: COLORS.paper,
};

const headerSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1.5,
};

const itemSx = {
    px: 2,
    py: 1.25,
    cursor: 'pointer',
    transition: 'background-color 120ms ease-out',
    '&:hover': { backgroundColor: COLORS.primaryLighter },
};

const StarredMessagesPanel = ({
    open,
    onClose,
    threadId = null,
    onJumpToMessage = null,
}) => {
    const dispatch = useDispatch();
    const { scrollToMessage } = useScrollToMessage();
    const [scope, setScope] = React.useState(SCOPE.THIS_CHAT);
    const [loading, setLoading] = React.useState(false);
    const [items, setItems] = React.useState([]);
    const [error, setError] = React.useState(null);

    const loadStarred = React.useCallback(async () => {
        if (!threadId) {
            setItems([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // ALL_CHATS scope is a future enhancement — backend currently
            // only exposes per-thread starred listing. We surface the toggle
            // now so the UX is in place, with a friendly empty-state for
            // ALL_CHATS until the cross-thread endpoint lands.
            if (scope === SCOPE.ALL_CHATS) {
                setItems([]);
                return;
            }
            const results = await dispatch(fetchStarredMessages(threadId));
            setItems(Array.isArray(results) ? results : []);
        } catch (err) {
            setError('Failed to load starred messages');
        } finally {
            setLoading(false);
        }
    }, [dispatch, scope, threadId]);

    React.useEffect(() => {
        if (open) {
            loadStarred();
        }
    }, [open, loadStarred]);

    const handleJump = (messageId) => {
        if (typeof onJumpToMessage === 'function') {
            onJumpToMessage(messageId);
        }
        scrollToMessage(messageId);
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{ paper: { sx: drawerPaperSx } }}
            ModalProps={{ keepMounted: false }}
        >
            <Box sx={headerSx}>
                <Icon
                    icon="mdi:star"
                    width={20}
                    height={20}
                    style={{ color: COLORS.warning }}
                />
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary, flex: 1 }}>
                    Starred messages
                </Typography>
                <IconButton size="small" onClick={onClose} aria-label="Close starred messages">
                    <Icon icon="mdi:close" width={18} height={18} />
                </IconButton>
            </Box>
            <Tabs
                value={scope}
                onChange={(_, value) => setScope(value)}
                variant="fullWidth"
                sx={{
                    minHeight: 36,
                    borderBottom: `1px solid ${COLORS.dividerLight}`,
                    '& .MuiTab-root': {
                        minHeight: 36,
                        textTransform: 'none',
                        fontSize: 13,
                        color: COLORS.textSecondary,
                    },
                    '& .Mui-selected': { color: COLORS.primary },
                    '& .MuiTabs-indicator': { backgroundColor: COLORS.primary },
                }}
            >
                <Tab value={SCOPE.THIS_CHAT} label="This chat" />
                <Tab value={SCOPE.ALL_CHATS} label="All chats" />
            </Tabs>

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={20} sx={{ color: COLORS.primary }} />
                    </Box>
                )}
                {!loading && error && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 13, color: COLORS.error }}>
                            {error}
                        </Typography>
                    </Box>
                )}
                {!loading && !error && items.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Icon
                            icon="mdi:star-outline"
                            width={36}
                            height={36}
                            style={{ color: COLORS.textDisabled }}
                        />
                        <Typography sx={{ mt: 1, fontSize: 13, color: COLORS.textSecondary }}>
                            {scope === SCOPE.THIS_CHAT
                                ? 'No starred messages in this chat yet.'
                                : 'Cross-chat starred view is coming soon.'}
                        </Typography>
                    </Box>
                )}
                {!loading && !error && items.map((item, idx) => {
                    const messageId = item?.message_id ?? item?.id;
                    const body = item?.body ?? '';
                    const starredByName = item?.starred_by?.name ?? 'Operator';
                    const timestamp = parsedDateTimeForWhatsapp(item?.created_time)
                        || parsedDateTimeForWhatsapp(item?.starred_at);

                    return (
                        <React.Fragment key={`starred-${messageId}-${idx}`}>
                            <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => handleJump(messageId)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleJump(messageId);
                                    }
                                }}
                                sx={itemSx}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: COLORS.textPrimary,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {body || <em style={{ color: COLORS.textDisabled }}>(no text)</em>}
                                </Typography>
                                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 11, color: COLORS.textSecondary }}>
                                        Starred by {starredByName}
                                    </Typography>
                                    {timestamp && (
                                        <Typography sx={{ fontSize: 11, color: COLORS.textDisabled }}>
                                            · {timestamp}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Divider sx={{ borderColor: COLORS.dividerLight }} />
                        </React.Fragment>
                    );
                })}
            </Box>
        </Drawer>
    );
};

StarredMessagesPanel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    threadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onJumpToMessage: PropTypes.func,
};

// Track B fixup B-5 — defaultProps on function components is deprecated
// in React 18.3+; defaults now live in the destructured signature.

export default StarredMessagesPanel;
